<template>
  <!-- 
    wrapper is needed for parent to target component element with resize observer
    v-card component returns #text object that is not valid for observer
  -->
  <div class="data-panel-wrapper" ref="dataPanel">
    <!-- Services directory (SD) hierarchy explanation:
    - Services directory metadata groups, e.g. Social Housing
    - Child level, services directory metadata, e.g. Small Providers
    - Child of metadata level is: services 
  -->
    <v-card flat :style="cardStyles">
      <!-- Header -->
      <v-row align="center" justify="space-between" class="pa-4">
        <v-col cols="auto" class="d-flex align-center">
          <v-btn
            class="text-h6 font-weight-medium text-left"
            variant="text"
            prepend-icon="mdi-table"
            @click="toggleCollapsed"
          >
            Service details
          </v-btn>
        </v-col>
        <!-- expand/collapse data section controls -->
        <v-col cols="auto" class="d-flex align-center">
          <v-icon class="mr-2" @click="toggleFullscreen">
            {{ isFullscreen ? "mdi-fullscreen-exit" : "mdi-fullscreen" }}
          </v-icon>
          <v-icon @click="toggleCollapsed">
            {{ isCollapsed ? "mdi-chevron-up" : "mdi-chevron-down" }}
          </v-icon>
        </v-col>
      </v-row>

      <v-divider />

      <!-- Scrollable Content Area -->
      <div
        v-show="!isCollapsed"
        class="d-flex flex-column flex-grow-1 px-4 overflow-y-auto grey-background"
        style="height: calc(100% - 80px)"
      >
        <i class="text text-left pt-4">
          The services data below is provided as a starting point for curating
          your own local services data. You may notice gaps where OCSI was
          unable to source more comprehensive national public datasets. Please
          always check the associated service information for known caveats to
          be aware of and please let us know if you think we can improve this
          information for our users.
        </i>
        <v-expand-transition :group="true">
          <div v-if="servicesGroupsAndMetaHierarchyLoaded">
            <!-- Services directory metadata groups, e.g. Social Housing -->
            <div
              v-for="group in servicesGroupsAndMetaHierarchy"
              :key="group.id"
            >
              <section v-if="serviceGroupHasSelectedMeta(group.id)">
                <div
                  class="d-flex align-center justify-space-between my-3 mt-8"
                >
                  <div class="d-flex align-center">
                    <h1
                      class="text-body-1 font-weight-bold text-grey-darken-3 mb-0"
                    >
                      {{ group.name }}
                    </h1>
                    <v-chip
                      v-if="group.official_source"
                      size="small"
                      color="primary"
                      class="ml-2"
                    >
                      <v-icon size="small" class="mr-1">
                        mdi-check-decagram
                      </v-icon>
                      Official Source
                    </v-chip>
                  </div>
                  <v-tooltip
                    location="top"
                    :disabled="isDemoAccount || fetchingServices"
                  >
                    <template #activator="{ props }">
                      <span v-bind="props">
                        <v-btn
                          icon
                          variant="text"
                          density="compact"
                          class="pa-0 ma-0 no-bg"
                          aria-label="Download services for custom areas for category"
                          @click="
                            downloadCustomAreaServices(
                              `services - ${group.name}`,
                              group,
                            )
                          "
                          :disabled="fetchingServices || isDemoAccount"
                        >
                          <v-icon>mdi-download</v-icon>
                        </v-btn>
                      </span>
                    </template>
                    <span v-if="isDemoAccount"
                      >Downloading unavailable during the trial</span
                    >
                    <span v-else-if="fetchingServices"
                      >Please wait for data to load</span
                    >
                    <span v-else
                      >Download all {{ group.name }} data as a CSV</span
                    >
                  </v-tooltip>
                </div>
                <!-- Child level, services directory metadata, e.g. Small Providers -->
                <v-expansion-panels
                  v-model="openGroupPanels[group.id]"
                  multiple
                >
                  <template
                    v-for="metadata in getSelectedMetadata(group)"
                    :key="metadata.id"
                  >
                    <!-- Show custom skeleton if this metadata is still loading -->
                    <div
                      v-if="
                        fetchingServices &&
                        loadingMetadataIds.includes(metadata.id)
                      "
                      class="skeleton-wrapper mb-4"
                    >
                      <div class="services-header-skeleton skeleton"></div>
                      <div class="services-body-skeleton skeleton"></div>
                    </div>

                    <!-- Show the actual expansion panel if loaded -->
                    <v-expansion-panel v-else :value="metadata.id">
                      <!-- title -->
                      <template #title>
                        <div class="d-flex align-center w-100">
                          <div class="d-flex align-center flex-grow-1">
                            <!-- selected metadata icon -->
                            <v-icon
                              :icon="
                                metadataIcons[metadata.id]?.icon
                                  ? `mdi-${metadataIcons[metadata.id].icon}`
                                  : ''
                              "
                              :color="metadataIcons[metadata.id]?.colour || ''"
                              class="metadata-icon mr-3"
                            ></v-icon
                            ><span>{{ metadata.service }}</span>

                            <!-- metadata information button -->
                            <v-btn
                              icon
                              density="compact"
                              class="ml-2"
                              :aria-label="
                                'Open service metadata information for ' +
                                metadata.service
                              "
                              @click.stop="
                                serviceInfoDialog = true;
                                selectedMetadata = metadata;
                              "
                              variant="text"
                            >
                              <v-icon icon="mdi-information" color="primary" />
                            </v-btn>

                            <!-- data will not appear on map warning -->
                            <v-tooltip
                              v-if="!metadata.plot_on_map"
                              text="This data cannot be shown on the map due to licensing restrictions."
                            >
                              <template #activator="{ props }">
                                <span v-bind="props">
                                  <v-icon
                                    class="ml-1"
                                    aria-label="Warning: This data cannot be shown on the map due to licensing restrictions."
                                    icon="mdi-alert"
                                    color="#D97706"
                                  />
                                </span>
                              </template>
                            </v-tooltip>
                          </div>

                          <!-- right-aligned count of services in metadata -->
                          <div class="ml-auto">
                            <v-chip
                              class="count-chip mr-4"
                              size="x-small"
                              density="compact"
                              >{{ getServiceCount(metadata.id) }}</v-chip
                            >
                          </div>
                        </div>
                      </template>
                      <!-- Child of metadata level is: services -->
                      <template #text>
                        <v-data-table
                          :headers="serviceTableHeaders"
                          :items="getGroupedServices(metadata.id)"
                          :items-per-page="10"
                          item-key="name"
                          class="p-0"
                          :no-data-text="`There are no '${metadata.service}' in the selected areas`"
                        >
                          <!-- eslint-disable-next-line vue/valid-v-slot -->
                          <template #header.actions>
                            <div class="text-right">
                              <div
                                class="d-flex flex-row align-center justify-end"
                                style="gap: 0"
                              >
                                <v-tooltip
                                  location="top"
                                  :disabled="fetchingServices"
                                  :text="`View ${metadata.service} data`"
                                >
                                  <template #activator="{ props }">
                                    <span v-bind="props">
                                      <v-btn
                                        icon
                                        variant="text"
                                        density="compact"
                                        class="pa-0 ma-0 no-bg"
                                        :aria-label="
                                          'Show table view for ' +
                                          metadata.service
                                        "
                                        @click="
                                          openTableDialog(metadata.id, 'Data')
                                        "
                                      >
                                        <v-icon>mdi-table</v-icon>
                                      </v-btn>
                                    </span>
                                  </template>
                                </v-tooltip>
                                <v-tooltip
                                  location="top"
                                  :disabled="isDemoAccount || fetchingServices"
                                >
                                  <template #activator="{ props }">
                                    <span v-bind="props">
                                      <v-btn
                                        icon
                                        variant="text"
                                        density="compact"
                                        class="pa-0 ma-0 ml-4 no-bg"
                                        @click="
                                          downloadCustomAreaServices(
                                            `services - ${group.name} - ${metadata.service}`,
                                            metadata.id,
                                          )
                                        "
                                        :disabled="
                                          fetchingServices || isDemoAccount
                                        "
                                        aria-label="Download services for custom areas"
                                      >
                                        <v-icon>mdi-download</v-icon>
                                      </v-btn>
                                    </span>
                                  </template>
                                  <span v-if="isDemoAccount"
                                    >Downloading unavailable during the
                                    trial</span
                                  >
                                  <span v-else-if="fetchingServices"
                                    >Please wait for data to load</span
                                  >
                                  <span v-else
                                    >Download {{ metadata.service }} data as a
                                    CSV</span
                                  >
                                </v-tooltip>
                              </div>
                            </div>
                          </template>
                          <!-- eslint-disable-next-line vue/valid-v-slot -->
                          <template #item.name="{ item }">
                            <div class="text-left">{{ item.name }}</div>
                          </template>
                          <!-- eslint-disable-next-line vue/valid-v-slot -->
                          <template #item.count="{ item }">
                            <div class="text-center">{{ item.count }}</div>
                          </template>
                          <!-- eslint-disable-next-line vue/valid-v-slot -->
                          <template #item.actions="{ item }">
                            <!-- service table row action buttons-->
                            <div
                              class="d-flex flex-row align-center justify-end"
                              style="gap: 0"
                            >
                              <!-- open services detail table -->
                              <v-tooltip
                                location="top"
                                :disabled="fetchingServices"
                                text="View the underlying data"
                              >
                                <template #activator="{ props }">
                                  <span v-bind="props">
                                    <v-btn
                                      icon
                                      variant="text"
                                      density="compact"
                                      class="pa-0 ma-0 no-bg"
                                      :aria-label="
                                        'Show table view for ' +
                                        (getMetadataByServices(item.services)
                                          ?.service || 'services')
                                      "
                                      @click="
                                        openTableDialog(item.services, 'Data')
                                      "
                                    >
                                      <v-icon>mdi-table</v-icon>
                                    </v-btn>
                                  </span>
                                </template>
                              </v-tooltip>
                              <!-- Zoom to area -->
                              <v-tooltip
                                location="top"
                                :disabled="fetchingServices"
                                text="Locate the services on the map"
                              >
                                <template #activator="{ props }">
                                  <span v-bind="props">
                                    <v-btn
                                      v-if="metadata.plot_on_map"
                                      icon
                                      variant="text"
                                      density="compact"
                                      class="pa-0 ma-0 ml-4 no-bg"
                                      aria-label="Zoom to services on map"
                                      @click="
                                        zoomToServices(group.name, item.name)
                                      "
                                    >
                                      <v-icon>mdi-magnify-plus-outline</v-icon>
                                    </v-btn>
                                  </span>
                                </template>
                              </v-tooltip>
                              <!-- download services for custom area -->
                              <v-tooltip
                                location="top"
                                :disabled="isDemoAccount || fetchingServices"
                              >
                                <template #activator="{ props }">
                                  <span v-bind="props">
                                    <v-btn
                                      icon
                                      variant="text"
                                      density="compact"
                                      class="pa-0 ma-0 ml-4 no-bg"
                                      aria-label="Download underlying data as a CSV"
                                      @click="
                                        () =>
                                          downloadCustomAreaServices(
                                            `services - ${group.name} - ${metadata.service} - ${item.name}`,
                                            item.services,
                                          )
                                      "
                                      :disabled="
                                        fetchingServices || isDemoAccount
                                      "
                                    >
                                      <v-icon>mdi-download</v-icon>
                                    </v-btn>
                                  </span>
                                </template>
                                <span v-if="isDemoAccount"
                                  >Downloading unavailable during the
                                  trial</span
                                >
                                <span v-else-if="fetchingServices"
                                  >Please wait for data to load</span
                                >
                                <span v-else
                                  >Download underlying data as a CSV</span
                                >
                              </v-tooltip>
                            </div>
                          </template>
                        </v-data-table>
                      </template>
                    </v-expansion-panel>
                  </template>
                </v-expansion-panels>
              </section>
            </div>
          </div>
        </v-expand-transition>
      </div>

      <DynamicDialog
        @dialogCancel="serviceInfoDialog = false"
        :show="serviceInfoDialog"
        @update:showDynamicDialog="(state) => (serviceInfoDialog = state)"
        max-width="800px"
        minHeight="100px"
        @dialogOk="serviceInfoDialog = false"
        contentClass="pa-4"
        okBtnColor="none"
        okBtnVariant="text"
        cancelBtnHidden
      >
        <template v-slot:title>About the service data</template>
        <template v-slot:content>
          <v-card-text class="text pa-0">
            <div class="mb-2">
              <b>{{ selectedMetadata.service }}</b>
            </div>
            <div v-for="field in metadataDialogFields" :key="field.key">
              <v-card-actions
                v-if="selectedMetadata[field.key]"
                class="px-0 py-2 align-start"
              >
                <v-icon
                  v-if="field.icon"
                  :icon="field.icon"
                  class="mt-2 mr-1"
                />
                <v-card-text class="pa-0">
                  <div>
                    <b>{{ field.name }}</b>
                  </div>
                  <div>
                    {{
                      field.key === "reference_date"
                        ? new Date(selectedMetadata[field.key])
                            .toISOString()
                            .split("T")[0]
                        : selectedMetadata[field.key]
                    }}
                  </div>
                </v-card-text>
              </v-card-actions>
            </div>
          </v-card-text>
        </template>
        <template v-slot:cancelBtnTitle>CLOSE</template>
      </DynamicDialog>

      <!-- Table Dialog -->
      <DynamicDialog
        @dialogCancel="tableDialog = false"
        :show="tableDialog"
        @update:showDynamicDialog="(state) => (tableDialog = state)"
        max-width="95vw"
        :height="`${serviceDialogHeight}px`"
        cancelBtnVariant="text"
        cancelBtnColor="none"
        :okBtnHidden="true"
        contentClass="pa-0"
        :persistent="false"
      >
        <template #title>Data</template>

        <template #content>
          <div class="pa-4 d-flex flex-column h-100">
            <!-- Fixed Header Area -->
            <div ref="dialogHeaderRef" class="mb-4">
              <!-- Service Header -->
              <div class="d-flex align-center justify-space-between mb-4">
                <div class="d-flex align-center">
                  <v-icon
                    v-if="currentTableMetadata"
                    :icon="
                      currentTableMetadata.icon
                        ? `mdi-${currentTableMetadata.icon}`
                        : 'mdi-circle'
                    "
                    :color="currentTableMetadata.colour || 'primary'"
                    class="mr-3"
                    size="24"
                  />
                  <div>
                    <div class="text-h6 font-weight-medium">
                      {{ currentTableMetadata?.service || "Services" }}
                    </div>
                    <div class="text-caption text-grey-darken-1">
                      {{ currentTableMetadata?.group_name || "" }}
                    </div>
                  </div>
                </div>
                <div class="d-flex align-center">
                  <v-btn
                    variant="text"
                    color="primary"
                    prepend-icon="mdi-information-outline"
                    class="mr-2"
                    @click="openTableDataDetails"
                  >
                    DETAILS
                  </v-btn>
                  <v-tooltip
                    location="top"
                    :disabled="!(isDemoAccount || fetchingServices)"
                  >
                    <template #activator="{ props }">
                      <span v-bind="props">
                        <v-btn
                          color="primary"
                          variant="elevated"
                          tile
                          prepend-icon="mdi-download"
                          @click="downloadTableData"
                          :disabled="fetchingServices || isDemoAccount"
                        >
                          DOWNLOAD CSV
                        </v-btn>
                      </span>
                    </template>
                    <span v-if="isDemoAccount"
                      >Downloading unavailable during the trial</span
                    >
                    <span v-else-if="fetchingServices"
                      >Please wait for data to load</span
                    >
                  </v-tooltip>
                </div>
              </div>

              <!-- Search -->
              <v-text-field
                v-model="tableSearch"
                prepend-inner-icon="mdi-magnify"
                placeholder="Search..."
                variant="outlined"
                density="compact"
                hide-details
                clearable
              />
            </div>

            <!-- Table with internal scroll, fixed header/footer -->
            <v-data-table
              :headers="tableDialogHeaders"
              :items="tableDialogData"
              :search="tableSearch"
              :items-per-page="50"
              :sort-by="[{ key: 'custom_area_name', order: 'asc' }]"
              class="elevation-0 border flex-grow-1"
              density="compact"
              :fixed-header="true"
              :fixed-footer="true"
              :height="`${serviceDialogTableHeight}px`"
              hover
            >
              <!-- no data slot -->
              <template #no-data>
                <div class="text-center pa-4">
                  <v-icon size="48" class="mb-2">mdi-table-remove</v-icon>
                  <div>No data available</div>
                </div>
              </template>
              <template
                v-for="field in tableDialogHeaders.map((h) => h.title)"
                :key="field"
                v-slot:[`item.${field}`]="{ item }"
              >
                <!-- url column value-->
                <template v-if="['URL', 'url', 'website'].includes(field)">
                  <a
                    v-if="item[field] && isUrlSafe(item[field])"
                    :href="formatUrl(item[field])"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-blue table-row"
                  >
                    {{ item[field] }}
                  </a>
                  <span v-else-if="item[field]">{{ item[field] }}</span>
                  <span v-else>-</span>
                </template>
                <!-- all other columns -->
                <template v-else>
                  <div class="table-row">
                    {{ formatTableValue(item[field]) }}
                  </div>
                </template>
              </template>
            </v-data-table>
          </div>
        </template>

        <template #cancelBtnTitle>CLOSE</template>
      </DynamicDialog>
    </v-card>
  </div>
</template>

<script>
import DynamicDialog from "@/components/DynamicDialog.vue";
import { exportCSVFile } from "@/mixins/ExportCSVFile";
export default {
  name: "ServicesDirectoryDataSection",
  components: {
    DynamicDialog,
  },
  props: {
    containerHeight: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      displayState: "hidden", // 'hidden'|'collapsed' | 'normal' | 'fullscreen'
      serviceTableHeaders: [
        {
          title: "Custom Area Name",
          key: "name",
          align: "start",
          class: "text-left",
          minWidth: "140px",
        },
        {
          title: "Count",
          key: "count",
          align: "center",
          class: "text-center",
          minWidth: "140px",
        },
        {
          title: "",
          key: "actions",
          align: "end",
          class: "text-right",
          sortable: false,
          minWidth: "140px",
        },
      ],
      loadingMetadataIds: [],
      env: process.env.VUE_APP_ENVIRONMENT,
      serviceInfoDialog: false,
      selectedMetadata: null,
      tableDialog: false,
      tableDialogData: [],
      tableDialogHeaders: [],
      tableDialogTitle: "",
      currentTableSource: null,
      currentTableMetadata: null,
      tableSearch: "",
      metadataDialogFields: [
        {
          name: "About",
          icon: "mdi-information-outline",
          key: "description",
        },
        {
          name: "Warnings",
          icon: "mdi-alert",
          key: "warnings",
        },
        {
          name: "Source",
          icon: "mdi-bullhorn-variant-outline",
          key: "source",
        },
        {
          name: "Reference date",
          icon: "mdi-calendar",
          key: "reference_date",
        },
      ],
      openGroupPanels: [], // Track expanded panels per group
      servicesGroupsAndMetaHierarchyLoaded: false,
      dataPanelLoaded: false,
    };
  },
  mounted() {
    // Ensure loadingMetadataIds contains all selected service ids (e.g. from URL)
    const selectedIds = this.selectedServices.map((s) => s.id);
    this.loadingMetadataIds = [...selectedIds];
    this.setDataPanelDisplayState();
  },
  computed: {
    isDemoAccount() {
      return this.$store.state.config.customClientConfig.client_type === "demo"
        ? true
        : false;
    },
    isHidden() {
      return this.displayState === "hidden";
    },
    isCollapsed() {
      return this.displayState === "collapsed";
    },
    isFullscreen() {
      return this.displayState === "fullscreen";
    },
    /** height of section based on state */
    cardHeight() {
      if (this.isFullscreen) return 100; // % (fullscreen)
      if (this.isCollapsed) return 60; // px (collapsed)
      if (this.isHidden) return 0; // px (hidden)
      return 66; // % (normal)
    },
    /** sets container height and position based on state (e.g. collapsed/fullscreen) */
    cardStyles() {
      const commonPosition =
        "position: absolute; bottom: 0; left: 0; right: 0;";
      const unit = this.isCollapsed ? "px" : "%";
      //return absolute positioning and height of component based on collapse state
      return `height: ${this.cardHeight}${unit}; ${commonPosition}`;
    },
    servicesGroupsAndMetaHierarchy: {
      get() {
        return this.$store.getters[
          "servicesDirectory/servicesGroupsAndMetaHierarchy"
        ];
      },
      set(val) {
        this.$store.commit(
          "servicesDirectory/setServicesGroupsAndMetaHierarchy",
          [...val],
        );
      },
    },
    selectedServices: {
      get() {
        return this.$store.getters["servicesDirectory/SDselectedServices"];
      },
      set(val) {
        this.$store.commit("servicesDirectory/setSDselectedServices", val);
      },
    },
    selectedServicesData: {
      get() {
        return this.$store.getters["servicesDirectory/SDfetchedServices"];
      },
      set(val) {
        this.$store.commit("servicesDirectory/setSDfetchedServices", val);
      },
    },
    selectedServicesDataCounts: {
      get() {
        return this.$store.getters["servicesDirectory/SDcounts"];
      },
      set(val) {
        this.$store.commit("servicesDirectory/setSDcounts", val);
      },
    },
    metadataIcons: {
      get() {
        return this.$store.getters["servicesDirectory/SDmetadataIcons"];
      },
      set(val) {
        this.$store.commit("servicesDirectory/setSDmetadataIcons", val);
      },
    },
    fetchingServices: {
      get() {
        return this.$store.getters["servicesDirectory/fetchingServices"];
      },
      set(val) {
        this.$store.commit("servicesDirectory/setFetchingServices", val);
      },
    },
    selectedCustomAreas: {
      get() {
        return this.$store.getters["servicesDirectory/selectedCustomAreas"];
      },
      set(val) {
        // the value is synced with URL as well as with vuex
        this.$router.push({
          query: {
            ...this.$route.query,
            selectedCustomAreas: JSON.stringify(val),
          },
        });
        this.$store.commit("servicesDirectory/setSelectedCustomAreas", val);
      },
    },
    serviceDialogHeight() {
      return 0.9 * window.innerHeight; // 90vh;
    },
    serviceDialogTableHeight() {
      return this.serviceDialogHeight - 500; //dialog height minus search
    },
  },
  watch: {
    /** emit a component height when the state changes (e.g. collapsed/fullscreen) */
    displayState: {
      handler() {
        this.emitDataSectionHeightChange();
      },
    },
    servicesGroupsAndMetaHierarchy(newVal) {
      if (Array.isArray(newVal) && newVal.length > 0) {
        this.servicesGroupsAndMetaHierarchyLoaded = true;
        this.setDataPanelDisplayState();
      } else {
        this.servicesGroupsAndMetaHierarchyLoaded = false;
        this.setDataPanelDisplayState();
      }
    },
    selectedServices: {
      handler(newVal, oldVal) {
        //previous selected service metadata ids
        const oldIds = new Set(oldVal.map((s) => s.id));
        //the new state of selected service metadata ids
        const newIds = newVal.map((s) => s.id);

        // ids that are newly selected
        const added = newIds.filter((id) => !oldIds.has(id));
        // ids that have been removed
        const removed = [...oldIds].filter((id) => !newIds.includes(id));

        // Add them only if they aren't already tracked (i.e. clicked off and on again)
        const newToAdd = added.filter(
          (id) => !this.loadingMetadataIds.includes(id),
        );

        // Remove any loading ids that are no longer selected
        if (removed.length) {
          this.loadingMetadataIds = this.loadingMetadataIds.filter(
            (id) => !removed.includes(id),
          );
        }

        //update loading list
        if (newToAdd.length) {
          this.loadingMetadataIds = [...this.loadingMetadataIds, ...newToAdd];
        }

        this.setDataPanelDisplayState();
      },
    },
    selectedCustomAreas: {
      handler() {
        this.setDataPanelDisplayState();
      },
    },
    fetchingServices(newVal, oldVal) {
      // When fetchingServices transitions from true to false, set panels
      if (oldVal === true && newVal === false) {
        this.setopenGroupPanels();
      }
    },
  },
  methods: {
    setDataPanelDisplayState() {
      if (
        !this.servicesGroupsAndMetaHierarchyLoaded ||
        this.selectedCustomAreas.length === 0 ||
        this.selectedServices.length === 0
      ) {
        this.displayState = "hidden";
      } else if (this.displayState == "hidden") {
        this.displayState = "normal"; // revert to normal if areas and services selected from hidden
      }
    },
    toggleCollapsed() {
      this.displayState =
        this.displayState === "collapsed" ? "normal" : "collapsed";
    },
    toggleFullscreen() {
      this.displayState =
        this.displayState === "fullscreen" ? "normal" : "fullscreen";
    },
    emitDataSectionHeightChange() {
      this.$nextTick(() => {
        const height = this.isCollapsed
          ? this.cardHeight
          : (this.cardHeight / 100) * this.containerHeight;

        //trigger data panel resize observer in parent and set map height
        if (this.displayState !== "hidden" && !this.dataPanelLoaded) {
          this.dataPanelLoaded = true;
          this.$emit("dataSectionLoaded");
        }

        this.$emit("sd-data-card-height", height);
      });
    },
    /**
     * (template function) returns boolean - whether supplied service group
     * id has selected service metadata items in servicesGroupAndMetaHierarchy
     **/
    serviceGroupHasSelectedMeta(id) {
      const group = this.servicesGroupsAndMetaHierarchy.find(
        (g) => g.id === id,
      );
      if (!group || !Array.isArray(group.metadata)) return false;

      return group.metadata.some((meta) => meta.selected === true);
    },
    /**
     * (template function) returns a filtered array of metadata items that are selected
     * for a given group object that contains the metadata
     */
    getSelectedMetadata(group) {
      if (!group || !Array.isArray(group.metadata)) return [];
      return group.metadata.filter((meta) => meta.selected === true);
    },
    /**
     * (template function) return a count of services from selectedServicesData
     * for a specific service meta id
     */
    getServiceCount(metadataId) {
      return this.selectedServicesDataCounts?.serviceCounts?.[metadataId] || 0;
    },
    /**
     * return a filtered array of services from selectedServicesData
     * for a specific service meta id
     */
    getMetadataServices(metadataId) {
      // if selectedServicesData is not yet loaded, return empty array
      if (
        !this.selectedServicesData ||
        !Array.isArray(this.selectedServicesData)
      ) {
        return [];
      }
      return this.selectedServicesData.filter(
        (service) => service.type_id === metadataId,
      );
    },
    /**
     * returns an array of services and counts grouped by custom area name
     * from selectedServicesData for a given metadata id
     */
    getGroupedServices(metadataId) {
      const services = this.getMetadataServices(metadataId);

      const grouped = services.reduce((acc, service) => {
        const areaName = service.custom_area_name || "None selected";
        if (!acc[areaName]) {
          acc[areaName] = { name: areaName, count: 0, services: [] };
        }
        acc[areaName].count++;
        acc[areaName].services.push(service);
        return acc;
      }, {});

      // Convert object to array
      return Object.values(grouped);
    },
    /**
     * Get all services for a specific metadata ID
     */
    getServicesForMetadata(metadataId) {
      return this.getGroupedServices(metadataId).flatMap((g) => g.services);
    },

    /**
     * Get all services for a specific group
     */
    getAllGroupServices(group) {
      const selectedMetadata = this.getSelectedMetadata(group);
      return selectedMetadata.flatMap((meta) =>
        this.getServicesForMetadata(meta.id),
      );
    },
    downloadCustomAreaServices(filename, source) {
      const services = this.getServicesData(source);
      const { processedData, csvHeaders } = this.processServicesData(services);
      exportCSVFile(csvHeaders, processedData, filename);

      this.sendDownloadAnalytics();
    },
    /**
     * Given a metadata id, returns the group id that the metadata belongs to.
     */
    getGroupIdByMetadataId(metadataId) {
      const group = this.servicesGroupsAndMetaHierarchy.find(
        (g) =>
          Array.isArray(g.metadata) &&
          g.metadata.some((meta) => meta.id === metadataId),
      );
      return group ? group.id : null;
    },
    /**
     * set expanded/collapsed panel state for each group
     */
    setopenGroupPanels() {
      // Auto-expand panel if only one service selected
      this.$nextTick(() => {
        if (this.selectedServices.length === 1) {
          const groupId = this.getGroupIdByMetadataId(
            this.selectedServices[0].id,
          );

          this.openGroupPanels[groupId] = [0];
        } else {
          this.openGroupPanels = []; // Reset if multiple services selected
        }

        /**
         * this opens all expandable panels with a data count greater than 0
         * leaving here for a bit in case somebody decides they actuall want this
         * feel free to delete 15/07/25
         **/
        // if (
        //   this.servicesGroupsAndMetaHierarchyLoaded &&
        //   this.selectedServices.length > 0
        // ) {
        //   this.selectedServices.forEach((service) => {
        //     //get group id for service metadata
        //     const groupId = this.getGroupIdByMetadataId(service.id);
        //     //get service count for metadata
        //     const serviceCount = this.getServiceCount(service.id);
        //     //add group id to openGroupPanels if doesn't already exist
        //     if (!this.openGroupPanels[groupId]) {
        //       this.openGroupPanels[groupId] = [];
        //     }
        //     //add selected service id to openGroupPanels for the group if there are services
        //     if (
        //       !this.openGroupPanels[groupId].includes(service.id) &&
        //       serviceCount > 0
        //     ) {
        //       this.openGroupPanels[groupId].push(service.id);
        //     }
        //   });
        // }
      });
    },
    /**
     * Get metadata information from services array. This is used to
     * find the metadata/group level info for a given service or array of services.
     */
    getMetadataByServices(services) {
      if (!services || services.length === 0) return null;

      // Check if servicesGroupsAndMetaHierarchy is available
      if (
        !this.servicesGroupsAndMetaHierarchy ||
        !Array.isArray(this.servicesGroupsAndMetaHierarchy)
      ) {
        return null;
      }

      const typeId = services[0].type_id; // type_id is sd metadata id

      // Find the metadata in servicesGroupsAndMetaHierarchy
      for (const group of this.servicesGroupsAndMetaHierarchy) {
        if (group.metadata) {
          const metadata = group.metadata.find((meta) => meta.id === typeId);
          if (metadata) {
            // Get the group name from the group object
            metadata.group_name = group.name || "";
            return metadata;
          }
        }
      }
      return null;
    },
    // Zoom to specific custom area
    zoomToServices(metadataGroup, customAreaName) {
      // get lat/lngs for selected group and custom area
      const filteredServiceLocations = this.selectedServicesData.filter(
        (item) =>
          item.custom_area_name === customAreaName &&
          item.group_name === metadataGroup,
      );

      const serviceCoords = filteredServiceLocations.map((item) => ({
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lng),
      }));

      const lats = serviceCoords.map((c) => c.lat);
      const lngs = serviceCoords.map((c) => c.lng);

      //bounds for selected service coords
      const bounds = {
        north: Math.max(...lats),
        south: Math.min(...lats),
        east: Math.max(...lngs),
        west: Math.min(...lngs),
      };

      this.$emit("zoomToServices", bounds);
    },
    /**
     * Process services data for both table display and CSV export
     * Returns an object with processed data, headers, and column info
     */
    processServicesData(services, forDownload = false) {
      // Mapping of field keys to display labels
      const headerMap = {
        custom_area_name: "Area Name",
        service_name: "Service",
        group_name: "Category",
      };

      // Flatten each service object by merging parsed labels
      const processed = services.map((service) => {
        let labels = {};
        try {
          labels = forDownload
            ? service.download_labels
              ? JSON.parse(service.download_labels)
              : {}
            : service.labels
              ? JSON.parse(service.labels)
              : {};
        } catch (e) {
          labels = {};
        }

        // Create a copy of service without postcode, but allow postcode from labels
        const serviceWithoutPostcode = { ...service };
        delete serviceWithoutPostcode.postcode;

        return {
          ...serviceWithoutPostcode,
          ...labels,
        };
      });

      // List of columns to exclude
      const exclude = [
        "id",
        "type_id",
        "lat",
        "lng",
        "plot_on_map",
        "labels",
        "download_labels",
      ];

      // Dynamically generate headers from all keys in the first row, excluding unwanted columns
      const headers =
        processed.length > 0
          ? Object.keys(processed[0]).filter((key) => !exclude.includes(key))
          : [];

      // Remove unwanted columns from each row and replace nulls with hyphen
      const filteredProcessed = processed.map((row) => {
        const filtered = {};
        headers.forEach((key) => {
          filtered[key] = row[key] === null ? "-" : row[key];
        });
        return filtered;
      });

      // Create table headers for Vue data table
      const tableHeaders = headers.map((key) => ({
        title: headerMap[key] || key,
        key: key,
        align: "start",
        sortable: true,
        minWidth: "140px",
      }));
      // Create CSV headers
      const csvHeaders = headers.map((key) => headerMap[key] || key);

      return {
        processedData: filteredProcessed,
        tableHeaders,
        csvHeaders,
        columnKeys: headers,
      };
    },

    /**
     * Open table dialog with processed service data
     */
    openTableDialog(source, title) {
      const services = this.getServicesData(source);
      const uniqueCustomAreas = [
        ...new Set(services.map((item) => item.custom_area_name)),
      ];
      const { processedData, tableHeaders } =
        this.processServicesData(services);

      this.currentTableSource = source;
      this.tableDialogData = processedData;
      this.tableDialogHeaders = tableHeaders;
      this.tableDialogTitle = title;

      // Set current metadata for display
      if (services && services.length > 0) {
        this.currentTableMetadata = this.getMetadataByServices(services);
      } else {
        this.currentTableMetadata = null;
      }

      this.tableSearch = "";
      this.tableDialog = true;

      //send analytics for opening table dialog
      this.$axios.put("/send-analytics", {
        event: "services_directory_data_dialog_opened",
        areas: uniqueCustomAreas.length, // count of custom areas in dialog
        services: this.selectedServices // comma-separated list of selected service names
          .map((service) => service.service)
          .join(","),
        servicesGroup: this.servicesGroupsAndMetaHierarchy //comma-separated list of selected service groups
          .filter(
            (group) =>
              Array.isArray(group.metadata) &&
              group.metadata.some((meta) => meta.selected === true),
          )
          .map((group) => group.name)
          .join(","),
      });
    },

    /**
     * Generic method to get services data based on different input types
     * @param {Object|Array|String|Number} source - Can be a group object, metadata ID, or services array
     * @returns {Array} - Array of service objects
     */
    getServicesData(source) {
      // If it's already an array of services, return as-is
      if (Array.isArray(source)) {
        return source;
      }

      // If it's a group object
      if (source && typeof source === "object" && source.metadata) {
        return this.getAllGroupServices(source);
      }

      // If it's a metadata ID (number or string)
      if (typeof source === "number" || typeof source === "string") {
        return this.getServicesForMetadata(source);
      }

      return [];
    },

    /**
     * Download the currently displayed table data as CSV
     */
    downloadTableData() {
      const { processedData, csvHeaders } = this.processServicesData(
        this.getServicesData(this.currentTableSource),
        true,
      );

      // Get metadata info for proper filename formatting
      let filename = "table-data";
      if (this.currentTableMetadata) {
        filename = `services - ${this.currentTableMetadata.group_name} - ${this.currentTableMetadata.service}`;
      }

      exportCSVFile(csvHeaders, processedData, filename);

      this.sendDownloadAnalytics();
    },

    /**
     * Format URL for href attribute by adding https:// prefix if needed
     * Includes security validation to prevent malicious URLs
     */
    formatUrl(url) {
      if (!url) return "";
      const trimmedUrl = url.trim();

      // Security: Prevent javascript: and data: URLs
      const lowerUrl = trimmedUrl.toLowerCase();
      if (
        lowerUrl.startsWith("javascript:") ||
        lowerUrl.startsWith("data:") ||
        lowerUrl.startsWith("vbscript:")
      ) {
        return "#"; // Return safe fallback
      }

      if (lowerUrl.startsWith("http")) {
        return trimmedUrl;
      }
      return `https://${trimmedUrl}`;
    },

    /**
     * Check if URL is safe to display as a clickable link
     */
    isUrlSafe(url) {
      if (!url) return false;
      const trimmedUrl = url.trim();
      const lowerUrl = trimmedUrl.toLowerCase();

      // Block dangerous protocols
      if (
        lowerUrl.startsWith("javascript:") ||
        lowerUrl.startsWith("data:") ||
        lowerUrl.startsWith("vbscript:") ||
        lowerUrl.startsWith("file:") ||
        lowerUrl.startsWith("ftp:")
      ) {
        return false;
      }

      // Only allow http/https/www patterns
      return lowerUrl.startsWith("http") || lowerUrl.startsWith("www");
    },

    /**
     * Open metadata details dialog for the currently displayed table data
     */
    openTableDataDetails() {
      // Get the metadata for the current table source
      const services = this.getServicesData(this.currentTableSource);
      if (services && services.length > 0) {
        const metadata = this.getMetadataByServices(services);
        if (metadata) {
          this.selectedMetadata = metadata;
          this.serviceInfoDialog = true;
        }
      }
    },

    sendDownloadAnalytics() {
      this.$axios.put("/send-analytics", {
        event: "services_directory_data_downloaded",
        areas: this.selectedCustomAreas.length, // Count of selected areas
        services: this.selectedServices // comma-separated list of selected service names
          .map((service) => service.service)
          .join(","),
        servicesGroup: this.servicesGroupsAndMetaHierarchy //comma-separated list of selected service groups
          .filter(
            (group) =>
              Array.isArray(group.metadata) &&
              group.metadata.some((meta) => meta.selected === true),
          )
          .map((group) => group.name)
          .join(","),
      });
    },
    /** guess dynamic datatable cell data type and format value e.g. to avoid decimals for integer values */
    formatTableValue(value) {
      if (value === null || value === undefined) return "";

      // Coerce numeric strings to numbers
      if (typeof value === "string" && !isNaN(value) && value.trim() !== "") {
        value = Number(value);
      }

      if (typeof value === "number") {
        // Format floats vs integers differently
        if (!Number.isInteger(value)) {
          return value.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            useGrouping: false, // No commas
          });
        } else {
          return value.toLocaleString(undefined, {
            useGrouping: false, // No commas
          });
        }
      } else if (typeof value === "string") {
        return value;
      } else {
        // Fallback for other types
        return String(value);
      }
    },
  },
};
</script>

<style scoped>
.v-card {
  transition: height 0.3s ease;
}

.skeleton-wrapper {
  width: 100%;
  display: block;
}

.services-header-skeleton {
  flex: 1;
  height: 28px;
  width: 100%;
  border-radius: 0px;
}

.services-body-skeleton {
  flex: 1;
  height: 150px;
  width: 100%;
  border-radius: 0px;
}

.skeleton {
  margin-bottom: 10px;
  /* background animation */
  background: linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.metadata-icon {
  filter: drop-shadow(1px 0px 0px rgba(0, 0, 0, 0.12))
    drop-shadow(-1px 0px 0px rgba(0, 0, 0, 0.12))
    drop-shadow(0px 1px 0px rgba(0, 0, 0, 0.12))
    drop-shadow(0px -1px 0px rgba(0, 0, 0, 0.12));
}

.grey-background {
  background-color: #f9f9f9 !important;
}

:deep(button.v-expansion-panel-title) {
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

:deep(.v-expansion-panel-title:hover) {
  background-color: #bfe0f8 !important;
}

:deep(.v-expansion-panel-text__wrapper) {
  padding: 0 !important;
}
.table-row {
  min-height: 64px !important;
  align-content: center;
}
</style>
